// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { Minus, Plus } from "lucide-react"
import {
  useI18n,
  formatEurFromCents,
  formatKcal,
  formatServings,
  formatWholeNumber,
  type I18nContextValue,
  type Locale,
} from "@/i18n"
import { Button } from "@/components/ui/button"
import { EstimatedBadge } from "@/components/state"
import { openAmyWithPrompt } from "@/lib/amyLauncher"
import {
  canApply,
  changeCostUnavailable,
  previewBlock,
  previewChange,
  stepTargets,
  type PortionPreviewState,
} from "@/lib/portionPreview"

export interface ServingsStepperProps {
  /** The page-owned portion-preview state machine (frontend/src/lib/portionPreview.ts). */
  state: PortionPreviewState
  /** The meal the preview describes — selects its change in the proposal (cost truth). */
  mealId: string
  /**
   * The meal's display title — EXACTLY what the page header shows — so the
   * Amy make-room prompt names the anchored meal in the user's own words
   * (ANCHOR invariant #13: the anchor must never be lost before Amy sees it).
   */
  title: string
  onStep: (direction: 1 | -1) => void
  onApply: () => void
  onCancel: () => void
  /** Re-read the plan after a STALE_REVISION apply refusal. */
  onReload: () => void
}

/** "1 serving" / "1.5 servings" — locale decimal separator, never a bare number. */
export function servingsLabel(servings: number, t: I18nContextValue["t"], locale: Locale): string {
  if (servings === 1) return t("views.recipe.servingsValueOne")
  return t("views.recipe.servingsValue", { servings: formatServings(servings, locale) })
}

/**
 * Portion stepper + preview card (ADR-055, founder acceptance 2026-09-04).
 * CONTROLLED and presentational: every value it shows — the stepper label,
 * the preview title, kcal, cost, the ingredient switch on the page — is
 * read from ONE `PortionPreviewState` owned by RecipePage, so they can
 * never describe different amounts (PREVIEW invariant #12). The page
 * performs the network calls (proposal + server-scaled portion) and
 * dispatches into the reducer with the request's sequence number; this
 * component only reports clicks.
 *
 * Real server-side recalculation (A1/D-1; T12: "client never computes
 * nutrition"): the `scale_servings` proposal scales this SAME recipe's
 * nutrition/cost from its per-serving data, never a different recipe. It is
 * a preview until the user chooses "Use N servings", which applies through
 * the same confirmation-token flow as every other plan change.
 *
 * Owner decision 2026-09-04 (one authoritative serving model): the value
 * moves ONLY through SUPPORTED_MEAL_SERVINGS (1 / 1.5 / 2) via the
 * reducer's `stepTargets`; the ends are disabled, so a 0.5 or 2.5 request
 * can never be built.
 */
export function ServingsStepper({ state, mealId, title, onStep, onApply, onCancel, onReload }: ServingsStepperProps) {
  const { t, locale } = useI18n()
  const { lower, higher } = stepTargets(state)
  const applying = state.phase === "applying"
  const minusDisabled = lower === null || applying
  const plusDisabled = higher === null || applying

  const endHint =
    lower === null
      ? t("views.recipe.stepperMin")
      : higher === null
        ? t("views.recipe.stepperMax")
        : null

  // The preview to render: present in preview/applying and in the apply
  // refusals that keep it (apply error, max-checkout conflict) — the reducer
  // clears it everywhere else, so `state.preview` IS the single source.
  const preview = state.preview
  const change = previewChange(state, mealId)
  // Portion owner's nutrition for exactly the selected serving; the mutation
  // owner's figure is the honest fallback when the composition is
  // incomplete (null nutrition, fail-closed) — one number, never two.
  const previewKcal = preview ? (preview.portion.nutrition?.kcal ?? change?.after?.kcal ?? null) : null
  // Cost truth: no estimate → say so, never a €0.00 that would read as free.
  const costUnavailable = changeCostUnavailable(change)
  // Blocked preview (ADR-055 acceptance 2): the selection's numbers and
  // ingredients stay visible (truthful preview) but the approve control is
  // replaced by the reason, built ONLY from the day figures the server
  // sent (`details`) — nothing is computed or parsed on the device.
  const block = preview ? previewBlock(state) : null
  const blockedSentence =
    block === null
      ? null
      : block.kind === "above"
        ? t("views.recipe.blockedAbove", {
            actual: formatWholeNumber(block.details.actual, locale),
            upper: formatWholeNumber(block.details.upperBound, locale),
          })
        : block.kind === "below"
          ? t("views.recipe.blockedBelow", {
              actual: formatWholeNumber(block.details.actual, locale),
              lower: formatWholeNumber(block.details.lowerBound, locale),
            })
          : t("views.recipe.blockedOther")

  const applyLabel =
    state.selected === 1
      ? t("views.recipe.previewApplyOne")
      : t("views.recipe.previewApply", { servings: formatServings(state.selected, locale) })
  const cancelLabel =
    state.planned === 1
      ? t("views.recipe.previewCancelOne")
      : t("views.recipe.previewCancel", { servings: formatServings(state.planned, locale) })
  // Acceptance 3 (2026-09-04): the CTA prefill carries the ANCHOR in user
  // words — this meal's title and the SELECTED portion — so Amy is asked to
  // keep it and adjust something else. Built from page state only; the
  // objective (the day range that blocked the portion) is Amy's default.
  const makeRoomPrompt =
    state.selected === 1
      ? t("views.recipe.blockedPromptOne", { meal: title })
      : t("views.recipe.blockedPrompt", { meal: title, servings: formatServings(state.selected, locale) })

  return (
    <div className="flex flex-col gap-2 rounded-bb-lg border border-line bg-paper p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-ink">{t("views.recipe.servingsLabel")}</p>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="-1"
            aria-disabled={minusDisabled}
            disabled={minusDisabled}
            title={lower === null ? t("views.recipe.stepperMin") : undefined}
            onClick={() => onStep(-1)}
          >
            <Minus className="size-4" aria-hidden="true" />
          </Button>
          <span data-testid="servings-value" className="min-w-6 text-center font-semibold text-ink">
            {servingsLabel(state.selected, t, locale)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="+1"
            aria-disabled={plusDisabled}
            disabled={plusDisabled}
            title={higher === null ? t("views.recipe.stepperMax") : undefined}
            onClick={() => onStep(1)}
          >
            <Plus className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {endHint && <p className="text-right text-xs text-ink-2">{endHint}</p>}

      {state.phase === "loading" && (
        <p className="text-sm text-ink-2">{t("views.recipe.recalcRequest")}</p>
      )}

      {state.phase === "error" && state.error === "preview" && (
        <p role="alert" className="text-sm text-destructive">
          {t("views.recipe.recalcError")}
        </p>
      )}

      {state.phase === "error" && state.error === "stale" && (
        <div role="alert" className="flex flex-col gap-2 rounded-bb-md bg-cream p-3 text-sm">
          <p className="text-ink">{t("views.recipe.previewStale")}</p>
          <Button type="button" variant="outline" className="min-h-11 self-start" onClick={onReload}>
            {t("views.plan.reloadButton")}
          </Button>
        </div>
      )}

      {state.phase === "applied" && (
        <p role="status" className="text-sm text-green">
          {state.planned === 1
            ? t("views.recipe.previewAppliedOne")
            : t("views.recipe.previewApplied", { servings: formatServings(state.planned, locale) })}
        </p>
      )}

      {preview && change?.after && (
        <div
          data-testid="portion-preview"
          className="flex flex-col gap-1 rounded-bb-md bg-cream p-3 text-sm"
        >
          <p className="font-medium text-ink">
            {t("views.recipe.recalcTitle", {
              from: formatServings(state.planned, locale),
              to: formatServings(state.selected, locale),
            })}
          </p>
          <p className="flex items-center gap-2 text-ink-2">
            {previewKcal !== null ? (
              <span data-testid="preview-kcal">{formatKcal(previewKcal, locale)}</span>
            ) : null}
            {previewKcal !== null ? " · " : null}
            {costUnavailable ? (
              <span data-testid="preview-cost-unavailable">{t("views.recipe.costUnavailable")}</span>
            ) : (
              <>
                <span data-testid="preview-cost">{formatEurFromCents(change.after.costCents, locale)}</span>
                <EstimatedBadge />
              </>
            )}
          </p>
          <p className="text-xs text-ink-2">{t("views.recipe.recalcNote")}</p>
          {block === null && <p className="text-xs text-ink-2">{t("views.recipe.recalcApplied")}</p>}

          {state.phase === "error" && state.error === "apply" && (
            <p role="alert" className="text-sm text-destructive">
              {t("views.recipe.previewApplyError")}
            </p>
          )}
          {state.phase === "error" && state.error === "max_conflict" && (
            <p role="alert" className="text-sm text-destructive">
              {t("views.recipe.previewMaxConflict")}
            </p>
          )}

          {block !== null ? (
            <div
              role="status"
              data-testid="portion-blocked"
              className="mt-2 flex flex-col gap-1.5 rounded-bb-md border border-gold/60 bg-gold/10 p-3"
            >
              <p className="text-sm font-semibold text-ink">{t("views.recipe.blockedTitle")}</p>
              <p className="text-sm text-ink">{blockedSentence}</p>
              <p className="text-xs text-ink-2">{t("views.recipe.blockedHint")}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                <Button
                  type="button"
                  className="min-h-11"
                  onClick={() => openAmyWithPrompt(makeRoomPrompt)}
                >
                  {t("views.recipe.blockedCta")}
                </Button>
                <Button type="button" variant="outline" className="min-h-11" onClick={onCancel}>
                  {cancelLabel}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                className="min-h-11"
                disabled={!canApply(state)}
                onClick={onApply}
              >
                {applying ? t("views.recipe.previewApplying") : applyLabel}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={applying}
                onClick={onCancel}
              >
                {cancelLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
